import React, { useState, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const i18n = defineMessages({
  clearMessages: { id: 'liveChatClear', defaultMessage: 'Clear messages' },
  deleteMessage: { id: 'liveChatDelete', defaultMessage: 'Delete message' },
  muteUser: { id: 'liveChatMute', defaultMessage: 'Mute' },
  unmuteUser: { id: 'liveChatUnmute', defaultMessage: 'Unmute' },
  removeUser: { id: 'liveChatRemove', defaultMessage: 'Remove user' },
  moderation: { id: 'liveChatModeration', defaultMessage: 'Moderation' },
  roleModerator: { id: 'liveChatRoleModerator', defaultMessage: 'Moderator' },
  roleYou: { id: 'liveChatRoleYou', defaultMessage: 'You' },
  roleModeratorYou: {
    id: 'liveChatRoleModeratorYou',
    defaultMessage: 'Moderator (you)',
  },
});

// Avatar badge color: navy for moderators, blue for regular users
const AVATAR_COLOR_MOD = '#1b3d6d';
const AVATAR_COLOR_USER = '#4a7fb5';

// Avatar symbol: 4 states based on role and self
// Moderator self: ★ (filled star), Moderator other: ☆ (empty star)
// Regular self: ● (filled circle), Regular other: ○ (empty circle)
function getAvatarSymbol(message) {
  if (message.role === 'moderator') {
    return message.isSelf ? '★' : '☆';
  }
  return message.isSelf ? '●' : '○';
}

function getAvatarColor(message) {
  return message.role === 'moderator' ? AVATAR_COLOR_MOD : AVATAR_COLOR_USER;
}

function getAvatarTitle(message, intl) {
  if (message.role === 'moderator' && message.isSelf) {
    return intl.formatMessage(i18n.roleModeratorYou);
  }
  if (message.role === 'moderator') {
    return intl.formatMessage(i18n.roleModerator);
  }
  if (message.isSelf) {
    return intl.formatMessage(i18n.roleYou);
  }
  return '';
}

const ChatMessage = ({ message, isModerator, onModAction, occupantRoles }) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Format timestamp: 24h for Finnish, AM/PM for others
  const formatTime = (timestamp) => {
    const d = new Date(timestamp);
    const locale = intl.locale || 'en';
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  if (message.retracted) {
    // Retracted messages disappear silently — no tombstone shown.
    return null;
  }

  if (message.role === 'system') {
    return (
      <div className="live-chat-message live-chat-message-system">
        <span className="live-chat-message-system-text">{message.text}</span>
      </div>
    );
  }

  const color = getAvatarColor(message);
  const avatarTitle = getAvatarTitle(message, intl);
  const targetRole =
    (occupantRoles && occupantRoles[message.nick]) || message.role;
  const targetIsModerator = targetRole === 'moderator';

  return (
    <div
      className={`live-chat-message${menuOpen ? ' live-chat-message-menu-open' : ''}`}
    >
      <span
        className="live-chat-avatar"
        style={{ backgroundColor: color }}
        title={avatarTitle || undefined}
      >
        {getAvatarSymbol(message)}
      </span>
      <div className="live-chat-message-body">
        <div className="live-chat-message-header">
          <span className="live-chat-message-nick">{message.nick}</span>
          <span className="live-chat-message-time">
            {formatTime(message.timestamp)}
          </span>
        </div>
        <span className="live-chat-message-text">{message.text}</span>
      </div>
      {isModerator && (
        <div className="live-chat-mod-wrapper" ref={menuRef}>
          <button
            className="live-chat-mod-trigger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={intl.formatMessage(i18n.moderation)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="live-chat-mod-menu" role="menu">
              {message.archiveId && (
                <button
                  className="live-chat-mod-menu-item"
                  role="menuitem"
                  onClick={() => {
                    onModAction('retract', message.archiveId);
                    setMenuOpen(false);
                  }}
                >
                  {intl.formatMessage(i18n.deleteMessage)}
                </button>
              )}
              <button
                className="live-chat-mod-menu-item"
                role="menuitem"
                onClick={() => {
                  onModAction('clear', message.nick);
                  setMenuOpen(false);
                }}
              >
                {intl.formatMessage(i18n.clearMessages)}
              </button>
              {!targetIsModerator && (
                <button
                  className="live-chat-mod-menu-item"
                  role="menuitem"
                  onClick={() => {
                    onModAction(
                      targetRole === 'visitor' ? 'unmute' : 'mute',
                      message.nick,
                    );
                    setMenuOpen(false);
                  }}
                >
                  {targetRole === 'visitor'
                    ? intl.formatMessage(i18n.unmuteUser)
                    : intl.formatMessage(i18n.muteUser)}
                </button>
              )}
              {!targetIsModerator && (
                <button
                  className="live-chat-mod-menu-item"
                  role="menuitem"
                  onClick={() => {
                    onModAction('remove', message.nick);
                    setMenuOpen(false);
                  }}
                >
                  {intl.formatMessage(i18n.removeUser)}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
